import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { Dialog } from '../components/Dialog';

const DESTINATION_TYPES = ['extension', 'ring_group', 'queue', 'ivr', 'voicemail', 'fax', 'external'];
const CONDITION_TYPES = ['always', 'caller_id', 'time', 'office_hours', 'holiday'];

export default function InboundRules() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ enabled: true, condition_type: 'always', destination_type: 'ring_group' });
  const { data: list = [] } = useQuery({ queryKey: ['inbound'], queryFn: () => api.inboundRules.list() });
  const { data: trunks = [] } = useQuery({ queryKey: ['trunks-for-inbound'], queryFn: () => api.trunks.list() });

  const create = useMutation({
    mutationFn: () => api.inboundRules.create(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inbound'] }); setOpen(false); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.inboundRules.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbound'] }),
  });
  const move = useMutation({
    mutationFn: ({ id, dir }: any) => api.inboundRules.move(id, dir),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbound'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Inbound Rules</h1>
        <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add Rule</button>
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th></th><th>Name</th><th>Trunk</th><th>DID</th><th>Condition</th><th>→</th><th>Destination</th><th>Order</th><th></th></tr></thead>
          <tbody>
            {(list as any[]).map((r: any, i: number) => (
              <tr key={r.rule_uuid} className="hover:bg-elevated">
                <td>
                  <button className="btn-ghost p-1" disabled={i===0} onClick={() => move.mutate({ id: r.rule_uuid, dir: 'up' })}><ChevronUp size={12} /></button>
                  <button className="btn-ghost p-1" disabled={i===list.length-1} onClick={() => move.mutate({ id: r.rule_uuid, dir: 'down' })}><ChevronDown size={12} /></button>
                </td>
                <td>{r.rule_name}</td>
                <td className="text-xs">{(trunks as any[]).find((t: any) => t.gateway_uuid === r.trunk_uuid)?.gateway_name || r.trunk_uuid?.slice(0,8)}</td>
                <td className="font-mono text-xs">{r.did_pattern || '*'}</td>
                <td>{r.condition_type}{r.condition_value ? `: ${r.condition_value}` : ''}</td>
                <td>{r.destination_type}</td>
                <td className="font-mono">{r.destination}</td>
                <td>{r.rule_order}</td>
                <td><button onClick={() => remove.mutate(r.rule_uuid)} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} title="Add Inbound Rule" size="lg"
        footer={<><button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button onClick={() => create.mutate()} className="btn-primary">Save</button></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rule name *" v={form.rule_name} set={(v: any) => setForm({ ...form, rule_name: v })} />
          <Select label="Trunk *" v={form.trunk_uuid} set={(v: any) => setForm({ ...form, trunk_uuid: v })}
            options={(trunks as any[]).map((t: any) => ({ value: t.gateway_uuid, label: t.gateway_name }))} />
          <Field label="DID pattern (regex)" v={form.did_pattern} set={(v: any) => setForm({ ...form, did_pattern: v })} mono />
          <Field label="Order" v={form.rule_order} set={(v: any) => setForm({ ...form, rule_order: parseInt(v, 10) })} type="number" />
          <Select label="Condition" v={form.condition_type} set={(v: any) => setForm({ ...form, condition_type: v })}
            options={CONDITION_TYPES.map((c) => ({ value: c, label: c }))} />
          <Field label="Condition value" v={form.condition_value} set={(v: any) => setForm({ ...form, condition_value: v })} mono />
          <Select label="Destination type *" v={form.destination_type} set={(v: any) => setForm({ ...form, destination_type: v })}
            options={DESTINATION_TYPES.map((c) => ({ value: c, label: c }))} />
          <Field label="Destination *" v={form.destination} set={(v: any) => setForm({ ...form, destination: v })} mono />
          <div className="col-span-2">
            <Field label="Description" v={form.description} set={(v: any) => setForm({ ...form, description: v })} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function Field({ label, v, set, mono = false, type = 'text' }: any) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <input className={`input ${mono ? 'font-mono' : ''}`} type={type} value={v ?? ''} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
function Select({ label, v, set, options }: any) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <select className="input" value={v || ''} onChange={(e) => set(e.target.value)}>
        <option value="">—</option>
        {options.map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
